/**
 * PlanningPrintDocument — aperçu + impression PDF de « Planification du personnel ».
 * Pages : 1-2 planning des effectifs (synthèse & mouvements / évolution du
 * personnel, via HrPlanningPrint), 3 masse salariale planifiée, 4 evolution
 * pluriannuelle. Même gabarit que PeoplePrintDocument (CSS Paged Media natif).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useP } from '../../i18n/useP';
import HrPlanningPrint from './print/HrPlanningPrint';
import PayrollMassPlanPrint from './print/PayrollMassPlanPrint';
import PayrollEvolutionPrint from './print/PayrollEvolutionPrint';
import './print/PeoplePrint.css';
import './PeoplePrintDocument.css';

// HrPlanningPrint émet 1 onDataReady (2 sections) ; les 2 autres 1 chacun.
const TOTAL_REPORTS = 3;

export default function PlanningPrintDocument({ activeTenant, year: yearProp, onClose }) {
  const { t } = useTranslation();
  const p = useP();
  const [ready, setReady] = useState(false);
  const [doneReports, setDoneReports] = useState(0);

  const year = yearProp ?? new Date().getFullYear();
  const editedAt = useMemo(() => new Date(), []);

  useEffect(() => {
    document.body.classList.add('people-printing');
    return () => document.body.classList.remove('people-printing');
  }, []);

  useEffect(() => {
    const handler = () => onClose();
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (doneReports >= TOTAL_REPORTS) setReady(true);
  }, [doneReports]);

  const handleDataReady = () => setDoneReports((n) => n + 1);

  const footerLeft = `${(activeTenant?.name || '').toUpperCase()} · ${editedAt.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  const cssVarStyle = { '--ppl-footer-left': `"${footerLeft.replace(/"/g, '\\"')}"` };

  const handlePrint = () => window.print();
  const dateStr = editedAt.toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' });

  return createPortal(
    <div className="ppl-print-overlay" style={cssVarStyle}>
      <div className="ppl-print-controls">
        <div className="ppl-print-controls__left">
          <span className="ppl-print-controls__title">
            {p('Aperçu — Planification du personnel')} · {activeTenant?.name}
          </span>
          {!ready && (
            <span className="ppl-print-controls__loading">
              ⟳ {t('common:status.loading').replace(/…$/, '')} ({doneReports}/{TOTAL_REPORTS})…
            </span>
          )}
        </div>
        <div className="ppl-print-controls__right">
          <button
            className="ppl-print-btn ppl-print-btn--print"
            onClick={handlePrint}
            disabled={!ready}
            title={ready ? p('Imprimer ou sauvegarder en PDF') : p('Patientez…')}
          >
            {ready ? p('⎙ Imprimer / PDF') : p('⟳ Chargement…')}
          </button>
          <button className="ppl-print-btn ppl-print-btn--close" onClick={onClose}>
            ✕ {t('common:actions.close')}
          </button>
        </div>
      </div>

      <div className="ppl-print__preview">
        <div className="ppl-print">
          <div className="ppl-cover">
            <div className="ppl-cover__brand">Palestre</div>
            <h1 className="ppl-cover__title">{p('Planification du personnel')}</h1>
            <div className="ppl-cover__meta">{activeTenant?.name} · {year}</div>
            <div className="ppl-cover__date">{p('Édité le ')}{dateStr}</div>
            <div className="ppl-cover__toc">
              <div className="ppl-cover__toc-title">{p('Sommaire')}</div>
              <ol className="ppl-cover__toc-list">
                <li>{p('Planning des effectifs — synthèse & mouvements')}</li>
                <li>{p('Planning des effectifs — évolution du personnel')}</li>
                <li>{t('phrases:p_3ca05e75ef')}</li>
                <li>{p('Evolution pluriannuelle')}</li>
              </ol>
            </div>
          </div>
          <HrPlanningPrint
            activeTenant={activeTenant}
            year={year}
            editedAt={editedAt}
            onDataReady={handleDataReady}
          />
          <PayrollMassPlanPrint
            activeTenant={activeTenant}
            year={year}
            editedAt={editedAt}
            onDataReady={handleDataReady}
          />
          <PayrollEvolutionPrint
            activeTenant={activeTenant}
            year={year}
            editedAt={editedAt}
            onDataReady={handleDataReady}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
